import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Tabs, TabsList, TabsTrigger, TabsContent } from '@devkit/ui';
import { Binary, RefreshCw } from 'lucide-react';
import { encodeBinary, decodeBinary } from '@devkit/tools-encoding';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

export default function BinaryEncoderPage() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEncode = useCallback(() => {
    setError(null);
    const result = encodeBinary(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to encode.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('binary-encoder', 'encode');
  }, [input]);

  const handleDecode = useCallback(() => {
    setError(null);
    const result = decodeBinary(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Invalid binary string.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('binary-encoder', 'decode');
  }, [input]);

  const handleSwap = useCallback(() => {
    setInput(output);
    setOutput('');
    setMode(mode === 'encode' ? 'decode' : 'encode');
  }, [output, mode]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('binary-encoder');
  }, []);

  const handleProcess = () => {
    if (mode === 'encode') handleEncode();
    else handleDecode();
  };

  return (
    <ToolLayout slug="binary-encoder">
      <Card>
        <CardContent className="p-6">
          <Tabs
            value={mode}
            onValueChange={(v) => {
              setMode(v as 'encode' | 'decode');
              setError(null);
            }}
          >
            <TabsList>
              <TabsTrigger value="encode">Encode</TabsTrigger>
              <TabsTrigger value="decode">Decode</TabsTrigger>
            </TabsList>

            <TabsContent value="encode">
              <p className="text-sm text-slate-400 mb-4">
                Convert text into its binary (base-2) representation, one byte per group.
              </p>
            </TabsContent>

            <TabsContent value="decode">
              <p className="text-sm text-slate-400 mb-4">
                Convert binary groups back into text. Each group must be 8 bits.
              </p>
            </TabsContent>
          </Tabs>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {mode === 'encode' ? 'Text Input' : 'Binary Input'}
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? 'Enter text to encode as binary...'
                  : '01001000 01101001 (groups of 8 bits separated by spaces)'
              }
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex gap-2 mb-4">
            <Button onClick={handleProcess} className="gap-2">
              <Binary className="w-4 h-4" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline" onClick={handleSwap} className="gap-2" disabled={!output}>
              <RefreshCw className="w-4 h-4" />
              Swap
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                {mode === 'encode' ? 'Binary Output' : 'Text Output'}
              </label>
              {output && <CopyButton text={output} onCopy={() => trackCopy('binary-encoder')} />}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Result will appear here...'}
              className={`w-full h-40 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y break-all ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
